import { Button, Flex, Text } from 'rebass';
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../../styles/theme';
import { LogoSvg } from './Logo';
import { OPTIONS_ANIMATION_DURATION } from './constants';
import { InputWithLabel } from '../../../components/TextInput';
import { isMobile } from 'react-device-detect';

const slideLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateY(15px);
  };
  100% {
    opacity: 1;
    transform: translateY(0px);
  };
`;

const Option = styled(Flex)`
  opacity: 0;
  animation: 400ms cubic-bezier(0.64, 0, 0.28, 1) 0s 1 normal forwards running ${slideLeft};
`;
const FootNote = styled(Flex)`
  opacity: 0;
  animation-delay: 1.24s;
  animation-direction: normal;
  animation-duration: 0.4s;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
  animation-name: ${slideLeft};
  animation-play-state: running;
  animation-timing-function: cubic-bezier(0.64, 0, 0.28, 1);
  box-sizing: border-box;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
`;

const Rect = styled.rect`
  transition: all var(0.3s) linear;
`;
const CheckboxWrap = styled(Flex)`
  border-color: rgba(0, 0, 0, 0.19);
  border-width: 2px;
  border-radius: 9px;
  border-style: solid;
  transition: all var(0.3s) linear;
  overflow: hidden;
`;

export const OPTIONS = [
  {
    id: 'pregnant',
    label: 'Currently pregnant',
  },
  {
    id: 'tryingpregnant',
    label: 'trying to become pregnant',
  },
  {
    id: 'breastfeeding',
    label: 'breastfeeding',
    description: 'breastfeeding/providing bottled breastmilk',
  },
  {
    id: 'cancer',
    label: 'Cancer or family history of certain conditions',
    description: 'Have an active cancer diagnosis or have had cancer within the last 5 years or personal or family history of medullary thyroid cancer',
  },
  {
    id: 'diabetesInsulin',
    label: 'Diabetes on insulin management',
    description: 'Type 1 diabetes or type 2 diabetes treated with insulin',
  },
  {
    id: 'heartConditions',
    label: 'Heart conditions or stroke',
    description: 'Heart attack or stroke within the last 12 months',
  },
  {
    id: 'diabetic',
    label: 'Diabetic retinopathy or pancreatitis',
    description: 'Diabetic retinopathy or current/past diagnosis of pancreatitis',
  },
  {
    id: 'noneConditions',
    label: 'None of these apply to me',
    isClearing: true,
  },
];

export const Conditions = ({ state, id, onChange, onContinue }) => {
  const [isValid, setIsValid] = useState(false);
  const computeValid = () => {
    for (let index = 0; index < OPTIONS.length; index++) {
      const element = OPTIONS[index];
      if (!!state[element.id]) {
        if (!element.hasDetails) {
          setIsValid(true);
          return;
        } else {
          if (!!state[element.detailsId]) {
            if (element.detailsType === 'year') {
              if (state[element.detailsId].length === 4) {
                setIsValid(true);
              }
            } else {
              setIsValid(true);
            }
            return;
          }
        }
      }
    }
    setIsValid(false);
  };

  useEffect(() => {
    computeValid();
  }, []);

  useEffect(
    () => {
      computeValid();
    },
    [state]
  );

  return (
    <Flex flex={1} flexDirection={'column'}>
      <LogoSvg />
      <Text style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 500 }} mb={4}>
        Do any of the following apply to you?
      </Text>
      {OPTIONS.map((x, i) => {
        const isSelected = (state || {})[x.id];
        return (
          <Option
            key={x.id}
            style={{
              animationDelay: `${1 + i * OPTIONS_ANIMATION_DURATION}s`,
            }}
          >
            <Flex flexDirection={'column'} flex={1}>
              <Flex
                mb={3}
                style={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  width: '100%',
                  borderRadius: '12px',
                  border: `${isSelected ? '2px solid rgb(249, 163, 148)' : '1px solid rgba(0, 0, 0, 0.15)'}`,
                  padding: isMobile ? (isSelected ? '14px 18px' : '15px 19px') : isSelected ? '18px' : '19px',
                  backgroundColor: isSelected ? 'rgba(255,255,255,0.43)' : 'transparent',
                  flex: 1,
                }}
                onClick={() => {
                  if (x.isClearing) {
                    const toDelete = [];
                    for (let index = 0; index < OPTIONS.length; index++) {
                      const element = OPTIONS[index];
                      if (element.id === x.id) continue;

                      if (!!element.hasDetails) {
                        toDelete.push(element.detailsId);
                      }
                      toDelete.push(element.id);
                    }
                    onChange(x.id, !isSelected, toDelete);
                  } else {
                    if (!!x.hasDetails) {
                      onChange(x.id, !isSelected, [x.detailsId, 'noneConditions']);
                      // onChange(x.detailsId, null);
                    } else {
                      onChange(x.id, !isSelected, [x.detailsId, 'noneConditions']);
                    }
                  }
                }}
              >
                <CheckboxWrap style={{ borderColor: state[x.id] ? theme.color.pink : '', alignSelf: 'baseline' }}>
                  <Flex flex={1} style={{ background: 'white', borderRadius: '3px', border: '2px solid #fdf5f2', overflow: 'hidden' }}>
                    <svg width="17" height="17" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg" fill={state[x.id] ? theme.color.pink : '#fdf5f2'}>
                      <Rect width="17" height="17" rx="5" />
                      {!!state[x.id] && <path d="M5 8.72727L7.1875 11L12 6" stroke="rgba(255,255,255, 1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                    </svg>
                  </Flex>
                </CheckboxWrap>
                <Flex flexDirection={'column'} ml={3} flex={1}>
                  
                  <Text fontWeight={500} fontSize={isMobile ? '14px' : ''}>{x.label}</Text>
                  {!!x.description && (
                    <Text fontWeight={300} flex={1} fontSize={isMobile ? '14px' : ''}>
                      {x.description}
                    </Text>
                  )}
                  {x.hasDetails &&
                    isSelected && (
                      <Flex
                        flexDirection={'row'}
                        mt={12}
                        flex={1}
                        style={{ border: `1px solid #344f79`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                      >
                        <Flex flex={1} flexDirection={'column'}>
                          <InputWithLabel
                            placeholder={`${x.detailsLabel} (YYYY)`}
                            style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                            value={state[x.detailsId]}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            type={'number'}
                            onChange={(e) => {
                              onChange(x.detailsId, e.target.value.toString());
                            }}
                          />
                          {!state[x.detailsId] ? (
                            <Flex ml={'12px'} mb={'12px'} alignItems={'center'}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM12 7C12.2652 7 12.5196 7.10536 12.7071 7.29289C12.8946 7.48043 13 7.73478 13 8V13C13 13.2652 12.8946 13.5196 12.7071 13.7071C12.5196 13.8946 12.2652 14 12 14C11.7348 14 11.4804 13.8946 11.2929 13.7071C11.1054 13.5196 11 13.2652 11 13V8C11 7.73478 11.1054 7.48043 11.2929 7.29289C11.4804 7.10536 11.7348 7 12 7ZM12 17C11.7348 17 11.4804 16.8946 11.2929 16.7071C11.1054 16.5196 11 16.2652 11 16C11 15.7348 11.1054 15.4804 11.2929 15.2929C11.4804 15.1054 11.7348 15 12 15C12.2652 15 12.5196 15.1054 12.7071 15.2929C12.8946 15.4804 13 15.7348 13 16C13 16.2652 12.8946 16.5196 12.7071 16.7071C12.5196 16.8946 12.2652 17 12 17Z"
                                  fill="#BD4244"
                                />
                              </svg>

                              <Text fontSize={'12px'} style={{ color: '#BD4244' }}>
                                This information is mandatory
                              </Text>
                            </Flex>
                          ) : (
                            <>
                              {x.detailsType === 'year' &&
                                state[x.detailsId].length !== 4 && (
                                  <Flex ml={'12px'} mb={'12px'} alignItems={'center'}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM12 7C12.2652 7 12.5196 7.10536 12.7071 7.29289C12.8946 7.48043 13 7.73478 13 8V13C13 13.2652 12.8946 13.5196 12.7071 13.7071C12.5196 13.8946 12.2652 14 12 14C11.7348 14 11.4804 13.8946 11.2929 13.7071C11.1054 13.5196 11 13.2652 11 13V8C11 7.73478 11.1054 7.48043 11.2929 7.29289C11.4804 7.10536 11.7348 7 12 7ZM12 17C11.7348 17 11.4804 16.8946 11.2929 16.7071C11.1054 16.5196 11 16.2652 11 16C11 15.7348 11.1054 15.4804 11.2929 15.2929C11.4804 15.1054 11.7348 15 12 15C12.2652 15 12.5196 15.1054 12.7071 15.2929C12.8946 15.4804 13 15.7348 13 16C13 16.2652 12.8946 16.5196 12.7071 16.7071C12.5196 16.8946 12.2652 17 12 17Z"
                                        fill="#BD4244"
                                      />
                                    </svg>

                                    <Text fontSize={'12px'} style={{ color: '#BD4244' }}>
                                      Year format must be YYYY (e.g. 2020)
                                    </Text>
                                  </Flex>
                                )}
                            </>
                          )}
                        </Flex>
                      </Flex>
                    )}
                </Flex>
              </Flex>
            </Flex>
          </Option>
        );
      })}
      <FootNote mb={4}
        style={{
          animationDelay: `${1 + OPTIONS.length * OPTIONS_ANIMATION_DURATION}s`,
        }}>
        <Button variant={'primary'} onClick={onContinue} disabled={!isValid}>
          Continue
        </Button>
      </FootNote>
    </Flex>
  );
};